import { maybe, rd } from '@passionware/monads';
import { SimpleReadOnlyEvent } from '@passionware/simple-event';
import {
  keepPreviousData,
  QueryClient,
  useInfiniteQuery,
  useQueries,
  useQuery
} from '@tanstack/react-query';
import { Resource } from '__types__/v1/models';
import { throttle } from 'lodash';
import { Batcher, createBatcher } from '../../../../v4/entities/common/batcher';
import { V1Action } from 'v4/migration/actionAsEventMiddleware';
import {
  ResourceApi,
  ResourceListResponse,
  ResourceQuery,
  resourceQueryUtils
} from '../../../api/resource/resource.api';
import { ensureIdleQuery } from '../../../platform/ensureIdleQuery';
import { paginatedResponseGetNextPageParam } from '../../../platform/infiniteQuery';
import {
  rowLevelValidation,
  type ValidatedRow
} from '../../../platform/row-level-validation';
import { SchemaError } from 'v4/entities/common/parseWithDecoratedError';
import { WithMessageService } from '../../internal/MessageService/MessageService';
import { ResourceNotFoundError, ResourceService } from './ResourceService';
import { WithErrorService } from '../../front/ErrorService/ErrorService';
import { WithServices } from '../../../platform/ts/services';

/**
 * Cached in place of a contact that failed validation: the query keeps it as
 * data, so a refetch doesn't turn its readers back to pending, while
 * {@link unwrapCachedResource} still hands them the {@link SchemaError}.
 */
class InvalidResource {
  constructor(readonly error: SchemaError) {}
}

type CachedResource = Resource | InvalidResource;

function unwrapCachedResource(item: CachedResource): Resource {
  if (item instanceof InvalidResource) {
    throw item.error;
  }
  return item;
}

export function createResourceService(
  config: WithServices<[WithErrorService, WithMessageService]> & {
    api: ResourceApi;
    queryClient: QueryClient;
    v1Event: SimpleReadOnlyEvent<V1Action>;
  }
): ResourceService {
  const {
    api,
    queryClient,
    v1Event,
    services: { errorService, messageService }
  } = config;
  const interceptor = errorService.withCategory('ResourceService');

  // Subscribe to system effects to handle cache invalidation on mutations
  messageService.reportSystemEffect.subscribeToRequest(async message => {
    for (const effect of message.request.scope) {
      switch (effect.entity) {
        case 'resource': {
          // Invalidate all resource list queries
          queryClient.removeQueries({
            queryKey: ['resources-v5', 'list'],
            type: 'inactive'
          });
          await queryClient.invalidateQueries({
            queryKey: ['resources-v5', 'list'],
            type: 'active'
          });
          // Invalidate the specific item query
          await queryClient.invalidateQueries({
            queryKey: ['resources-v5', 'item', effect.entityId]
          });
        }
      }
    }
    message.sendResponse();
  });

  const batcher: Batcher<
    Resource['id'],
    ValidatedRow<Resource>[],
    CachedResource
  > = createBatcher(
    async ids => {
      if (ids.length === 0) {
        return [];
      }
      return await interceptor.wrap(() => api.getItems(ids))();
    },
    (rows, id) => {
      const row = rows.find(r => r.id === id);
      if (!row) {
        throw new ResourceNotFoundError(id);
      }
      if (row.isValid) {
        return row.data;
      }
      // Only this id fails; its batch-mates resolve as usual.
      const error = new SchemaError(row.error, row.rawItem, row.rawItem);
      errorService.reportError('ResourceService', error);
      return new InvalidResource(error);
    }
  );

  async function fetchItem(
    id: Resource['id'],
    signal: AbortSignal
  ): Promise<CachedResource> {
    const batched = await batcher.fetch({ payload: id, signal });
    if (!batched) {
      throw new Error(`Resource with id ${id} not found`);
    }
    return batched;
  }

  const invalidateAll = throttle(
    async () => {
      queryClient.removeQueries({
        queryKey: ['resources-v5'],
        type: 'inactive'
      });
      await queryClient.invalidateQueries({
        queryKey: ['resources-v5'],
        type: 'active'
      });
    },
    // we don't want to invalidate the cache too often
    3000,
    {
      // after period of inactivity, we want to invalidate the cache immediately
      leading: true,
      // we want to make sure that last invalidation is executed
      trailing: true
    }
  );

  v1Event.addListener(async event => {
    switch (event.type) {
      case 'CREATE_CONTACT_SUCCESS':
      case 'UPDATE_CONTACT_SUCCESS':
      case 'ARCHIVE_CONTACT_SUCCESS':
      case 'UNARCHIVE_CONTACT_SUCCESS':
      case 'MERGE_RESOURCE_SUCCESS':
        await invalidateAll();
        return;
    }
  });

  // Subscribe to cache events for guest shortlist data
  messageService.reportCache.addListener(event => {
    for (const entity of event.entities) {
      switch (entity.entity) {
        case 'resource':
          queryClient.setQueryData(
            ['resources-v5', 'item', entity.payload.id],
            entity.payload
          );
          break;
      }
    }
  });

  /** A list page; each resource on it seeds its item, so opening one needs no request. */
  async function fetchList(query: ResourceQuery, signal: AbortSignal) {
    const result = await api.getList(query, signal);
    const validItems = rowLevelValidation.validDataFromRows(result.items);
    for (const resource of validItems) {
      queryClient.setQueryData(['resources-v5', 'item', resource.id], resource);
    }
    return result;
  }

  return {
    useList: query =>
      ensureIdleQuery(
        query,
        useQuery(
          {
            queryKey: ['resources-v5', 'list', query],
            queryFn: interceptor.wrap(ctx => fetchList(query!, ctx.signal)),
            enabled: maybe.isPresent(query)
          },
          queryClient
        )
      ),
    useListInfinite: query =>
      useInfiniteQuery(
        {
          // the pages start at 0 whatever page the query is on: one cache per list
          queryKey: [
            'resources-v5',
            'list',
            'infinite',
            maybe.map(query, resourceQueryUtils.resetPage)
          ],
          enabled: maybe.isPresent(query),
          initialPageParam: 0,
          queryFn: interceptor.wrap(
            ({
              pageParam,
              signal
            }: {
              pageParam: number;
              signal: AbortSignal;
            }) =>
              fetchList(
                { ...query!, page: { ...query!.page, page: pageParam } },
                signal
              )
          ),
          getNextPageParam: (
            lastPage: ResourceListResponse,
            _pages,
            lastPageParam
          ) =>
            paginatedResponseGetNextPageParam(
              lastPage,
              lastPageParam,
              query!.page.pageSize
            ),
          placeholderData: maybe.isPresent(query)
            ? keepPreviousData
            : undefined,
          // the app's default `staleTime` is infinite: a list read anew is read again
          refetchOnMount: 'always'
        },
        queryClient
      ),
    useListPages: (query, pageIndices, pageSize) =>
      useQueries(
        {
          queries: maybe.isPresent(query)
            ? pageIndices.map(page => {
                const pageQuery = {
                  ...query,
                  page: { page, pageSize }
                };
                return {
                  queryKey: ['resources-v5', 'list', pageQuery],
                  queryFn: interceptor.wrap(
                    ({ signal }: { signal: AbortSignal }) =>
                      fetchList(pageQuery, signal)
                  )
                };
              })
            : []
        },
        queryClient
      ),
    ensureList: query =>
      queryClient.fetchQuery({
        queryKey: ['resources-v5', 'list', query],
        queryFn: interceptor.wrap(ctx => api.getList(query, ctx.signal))
      }),
    useItem: id =>
      ensureIdleQuery(
        id,
        useQuery<CachedResource, Error, Resource>(
          {
            queryKey: ['resources-v5', 'item', id],
            queryFn: ({ signal }) => {
              if (!id) {
                throw new Error('Resource ID is required');
              }
              return fetchItem(id, signal);
            },
            select: unwrapCachedResource,
            enabled: maybe.isPresent(id)
          },
          queryClient
        )
      ),
    ensureItem: id =>
      queryClient
        .fetchQuery({
          queryKey: ['resources-v5', 'item', id],
          queryFn: ({ signal }): Promise<CachedResource> =>
            fetchItem(id, signal)
        })
        .then(unwrapCachedResource),
    useItemsById: ids =>
      rd.combineAll(
        useQueries({
          queries: ids.map(id => ({
            queryKey: ['resources-v5', 'item', id],
            queryFn: ({ signal }: { signal: AbortSignal }) =>
              fetchItem(id, signal),
            select: unwrapCachedResource
          }))
        })
      )
  };
}
